import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Overlay,
} from '@radix-ui/react-dialog';
import { ChevronSmallDown, ChevronSmallUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import clsx from 'clsx';
import { atom, useAtom } from 'jotai';
import { UnmountClosed } from 'react-collapse';
import { cn } from 'src/utils/classNamesHelper';

import { useCookies } from 'src/apps/header/cookies';
import { ConsentLevel } from 'src/consent';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { Trans } from '@lingui/react/macro';
import { useLocation } from '@tanstack/react-router';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { isBlacklistedCookieBannerPath } from 'src/utils/urls/urls';
import { Button } from './Button';
import { LinkExternal } from './LinkExternal';

const cookieBannerAtom = atom<boolean>(false);

export const useCookieBanner = () => useAtom(cookieBannerAtom);

export const CookieBanner = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useCookieBanner();
  const [cookiesAccepted, acceptCookies] = useCookies();
  const location = useLocation();

  // Automatically close the dialog if cookies are already accepted
  useEffect(() => {
    if (!cookiesAccepted && !isBlacklistedCookieBannerPath(location.pathname)) {
      setShowCookieBanner(true);
    }
  }, [cookiesAccepted, location.pathname]);

  const handleAcceptCookies = (level: ConsentLevel) => {
    acceptCookies(level);
    setShowCookieBanner(false);
  };

  return (
    <Dialog
      open={showCookieBanner}
      onOpenChange={(newState) => {
        // Don't close the banner if cookie level is not set
        if (newState === false && !cookiesAccepted) return;

        setShowCookieBanner(newState);
      }}
    >
      <Overlay
        className={clsx(
          'fixed inset-0 z-[60] bg-gray-900/60 transition-all',
          'motion-safe:data-[state=closed]:animate-fade-out motion-safe:data-[state=open]:animate-fade-in',
          'motion-reduce:data-[state=closed]:opacity-0 motion-reduce:data-[state=open]:opacity-1'
        )}
        onClick={(e) => e.stopPropagation()}
      />
      <DialogContent
        className={cn(
          'rounded-sm fixed top-1/2 left-1/2 z-[61] max-h-[calc(100%-32px)] w-[calc(100%-32px)] max-w-lm overflow-y-auto bg-neutral-light-2 px-16 pt-48 pb-32 text-center shadow-xl focus:outline-hidden ls:py-48 lm:px-32',
          'motion-safe:data-[state=closed]:animate-modal-out motion-safe:data-[state=open]:animate-modal-in',
          'motion-reduce:-translate-x-1/2 motion-reduce:-translate-y-1/2'
        )}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement)?.focus();
        }}
      >
        <div
          className="flex flex-col gap-24 lm:max-w-5xl"
          data-cy="cookie-banner"
        >
          <div>
            <DialogTitle>
              <Typography variant="heading-subtle-xl" className="text-center">
                <Trans context="web cookies">Cookies</Trans>
              </Typography>
            </DialogTitle>

            <Typography variant="body-md" className="mt-16">
              <Trans>
                We use cookies to continuously improve your experience across
                our websites. By using this website, you consent to our strictly
                necessary cookies.
              </Trans>
            </Typography>
            <div className="mt-16 flex flex-row justify-center gap-4">
              <Button
                variant="tertiary-neutral"
                onClick={() => setShowDetails((showDetails) => !showDetails)}
                data-cy="cookie-banner-show-details"
                aria-controls="cookie-banner-details-content"
                aria-expanded={showDetails}
              >
                {showDetails ? (
                  <span>
                    <Typography
                      variant="interface-md"
                      fontWeight="semibold"
                      leadingNone
                      className="inline"
                    >
                      <Trans>Hide details</Trans>
                    </Typography>
                    <ChevronSmallUp className="ml-4 inline max-w-20" />
                  </span>
                ) : (
                  <span>
                    <Typography
                      variant="interface-md"
                      fontWeight="semibold"
                      leadingNone
                      className="inline"
                    >
                      <Trans>Show details</Trans>
                    </Typography>
                    <ChevronSmallDown className="ml-4 inline max-w-20" />
                  </span>
                )}
              </Button>
            </div>
            <UnmountClosed isOpened={showDetails}>
              <div
                className="m-0 py-8"
                data-cy="cookie-banner-details"
                id="cookie-banner-details-content"
              >
                <Typography variant="body-md" className="mt-16">
                  <Trans>
                    Necessary cookies enable basic functions like page
                    navigation and access to secure areas. These cookies are
                    essential for the website to function.
                  </Trans>
                </Typography>
                <Typography variant="body-md" className="mt-16">
                  <Trans>
                    Statistical cookies help us analyze traffic and understand
                    customer needs. This data is pseudonymised and will not be
                    used by reMarkable to identify you. Selecting &apos;Accept
                    all&apos; enables these cookies for this and other
                    reMarkable sites.
                  </Trans>
                </Typography>
                <Typography variant="body-md" className="mt-16">
                  <Trans>
                    See our{' '}
                    <LinkExternal
                      to={SUPPORT_URL.COOKIE_POLICY}
                      data-cy="cookie-policy-link"
                      inline
                      componentLocation={
                        ComponentLocations.FOOTER.COOKIE_POLICY
                      }
                    >
                      cookie policy
                    </LinkExternal>{' '}
                    for more information.
                  </Trans>
                </Typography>
              </div>
            </UnmountClosed>
          </div>

          <div className="flex w-full flex-col justify-center gap-16 lm:flex-row">
            <Button
              size="medium"
              variant="secondary"
              data-cy="cookie-banner-necessary"
              className="w-full lm:w-fit"
              onClick={() => handleAcceptCookies(ConsentLevel.NECESSARY)}
            >
              <Trans context="cookie consent">Use necessary only</Trans>
            </Button>
            <Button
              size="medium"
              className="w-full lm:w-fit"
              data-cy="cookie-banner-all"
              onClick={() => handleAcceptCookies(ConsentLevel.ALL)}
            >
              <Trans context="cookie consent">Accept all</Trans>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
